import { BaseFlowBlockEventDefinitionSchema } from 'flow-schema';
import { z } from 'zod';

import { api, assertNever, isDefined } from 'common';

export const BlockType = api.FlowDefinition.BlockType;
export type BlockType = api.FlowDefinition.BlockType;
export type AnyBlockType = api.FlowDefinition.AnyBlockType;
export const ObsoleteBlockType = api.FlowDefinition.ObsoleteBlockType;
export type ObsoleteBlockType = api.FlowDefinition.ObsoleteBlockType;
export const SpecialBlockType = api.FlowDefinition.SpecialBlockType;
export type SpecialBlockType = api.FlowDefinition.SpecialBlockType;
export const UnknownBlockType = api.FlowDefinition.UnknownBlockType;
export type UnknownBlockType = api.FlowDefinition.UnknownBlockType;
export const BlockExecutionState = api.FlowRun.FlowBlockState;
export type BlockExecutionState = api.FlowRun.FlowBlockState;
export const FlowRunBlockDataSource = api.FlowDefinition.FlowRunBlockDataSource;
export type FlowRunBlockDataSource = api.FlowDefinition.FlowRunBlockDataSource;
export const ProducedType = api.FlowDefinition.ProducedType;
export type ProducedType = api.FlowDefinition.ProducedType;
export const FlowDecorationType = api.FlowDefinition.FlowDecorationType;
export type FlowDecorationType = api.FlowDefinition.FlowDecorationType;
export const { SingletonBlockTypes } = api.FlowDefinition;

/**
 * Consider adding placeholder as a different FlowNodeType
 * If added, make sure that you can switch between Block and Placeholder in the flow editor, including optional error edges wired
 * to blocks.
 */
export enum FlowNodeType {
  Block = 'Block',
  Placeholder = 'Placeholder',
  StickyNote = 'StickyNote',
  GroupNode = 'GroupNode',
}

export function getFlowNodeTypeForFlowDecorationType(
  decorationType: FlowDecorationType
): FlowNodeType {
  switch (decorationType) {
    case FlowDecorationType.StickyNote:
      return FlowNodeType.StickyNote;
    case FlowDecorationType.GroupNode:
      return FlowNodeType.GroupNode;
    default:
      assertNever('Unknown FlowDecorationType', decorationType);
  }
}

export function getFlowDecorationTypeForFlowNodeType(
  decorationType: FlowNodeType
): FlowDecorationType {
  switch (decorationType) {
    case FlowNodeType.StickyNote:
      return FlowDecorationType.StickyNote;
    case FlowNodeType.GroupNode:
      return FlowDecorationType.GroupNode;
    case FlowNodeType.Block:
      throw new Error('Cannot convert Block to FlowDecorationType');
    case FlowNodeType.Placeholder:
      throw new Error('Cannot convert Placeholder to FlowDecorationType');
    default:
      assertNever('Unknown FlowNodeType', decorationType);
  }
}

/**
 * `UiNodeType`s are only valid when editing a flow definition. For example, the `Placeholder` node type is used to
 * choose a BlockType to add to the flow. In addition, a block can be converted back to a Placeholder.
 * Its `NodeType` does not exist in the server. React flow needs it in the map of `nodeTypes`.
 */
export enum UiPlaceholderBlockType {
  BasePlaceholder = 'BasePlaceholder',
  Placeholder = 'Placeholder',
}

const UiBlockTypeSchema = z
  .enum(BlockType)
  .or(z.enum(UnknownBlockType))
  .or(z.enum(SpecialBlockType))
  .or(z.enum(ObsoleteBlockType));
export type UiBlockType = z.infer<typeof UiBlockTypeSchema>;

export const UiBlockType = {
  ...BlockType,
  ...UnknownBlockType,
  ...SpecialBlockType,
  ...ObsoleteBlockType,
};

export const AllUiBlockTypes = new Set(Object.values(UiBlockType));

export const BaseReactFlowBlockDataSchema = z.object({
  description: z.string().optional(),
  areExtraTargetHandlesVisible: z.boolean().optional(),
});
export type BaseReactFlowBlockData = z.infer<typeof BaseReactFlowBlockDataSchema>;

export const ReactFlowBlockTypeDataObjectSchema = z
  .object({
    uiBlockType: UiBlockTypeSchema,
    label: z.string().min(1),
    config: z.record(z.string(), z.any()).optional(),
    milestoneName: z.string().optional(),
    events: z.array(BaseFlowBlockEventDefinitionSchema).optional(),
    stats: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
    mockDataEnabled: z.boolean().optional(),
    mockData: z.string().optional(),
    returnType: z.enum(ProducedType).optional(),
    run: z
      .object({
        state: z.enum(BlockExecutionState),
        returnValue: z.unknown(),
        metadata: z
          .object({
            source: z
              .object({
                type: z.enum(api.FlowDefinition.FlowRunBlockDataSource),
              })
              .optional(),
          })
          .passthrough()
          .nullable(),
        error: z.string().nullable(),
        dataSizeBytes: z.number().nullable(),
        startedAt: z.string().nullable(),
        endedAt: z.string().nullable(),
      })
      .optional(),
  })
  .merge(BaseReactFlowBlockDataSchema);

export function refineMockData(
  data: { mockDataEnabled?: boolean; mockData?: string },
  context: z.RefinementCtx
) {
  if (!data.mockDataEnabled || !isDefined(data.mockData) || data.mockData === '') {
    return;
  }
  if (data.mockData.length > 1_000) {
    // Validation will be limited to a subset of the string to avoid performance issues with large mock data.
    if (data.mockData.at(0) === '{' && data.mockData.at(-1) === '}') {
      return;
    }
    if (data.mockData.at(0) === '[' && data.mockData.at(-1) === ']') {
      return;
    }
    if (data.mockData.at(0) === '"' && data.mockData.at(-1) === '"') {
      return;
    }
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message:
        'mockData must be a valid JSON string starting with {, [, or " if it is longer than 1000 characters',
    });
    return;
  }
  try {
    JSON.parse(data.mockData);
  } catch {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'mockData must be valid JSON',
      path: ['mockData'],
    });
  }
}

export const ReactFlowBlockTypeDataObjectWithoutConfigSchema =
  ReactFlowBlockTypeDataObjectSchema.omit({ config: true }).superRefine(refineMockData);
export type ReactFlowBlockTypeDataObjectWithoutConfig = z.infer<
  typeof ReactFlowBlockTypeDataObjectWithoutConfigSchema
>;

export const ReactFlowBlockTypeDataSchema =
  ReactFlowBlockTypeDataObjectSchema.superRefine(refineMockData);
export type ReactFlowBlockTypeData = z.infer<typeof ReactFlowBlockTypeDataSchema>;

export const ReactFlowPlaceholderDataSchema = z
  .object({
    uiBlockType: z.enum(UiPlaceholderBlockType),
  })
  .merge(BaseReactFlowBlockDataSchema);
export type ReactFlowPlaceholderData = z.infer<typeof ReactFlowPlaceholderDataSchema>;

export function isUnknownBlockType(uiBlockType?: UiBlockType): uiBlockType is UnknownBlockType {
  return Object.values(UnknownBlockType).includes(uiBlockType as UnknownBlockType);
}

export function isSpecialBlockType(uiBlockType?: UiBlockType): uiBlockType is SpecialBlockType {
  return Object.values(SpecialBlockType).includes(uiBlockType as SpecialBlockType);
}

export function isObsoleteBlockType(uiBlockType?: UiBlockType): uiBlockType is ObsoleteBlockType {
  return Object.values(ObsoleteBlockType).includes(uiBlockType as ObsoleteBlockType);
}

export function isBlockType(uiBlockType?: UiBlockType): uiBlockType is BlockType {
  return Object.values(BlockType).includes(uiBlockType as BlockType);
}

export function isStartBlockType(uiBlockType?: UiBlockType): boolean {
  return uiBlockType === BlockType.Start;
}

export function isOutputBlockType(uiBlockType?: UiBlockType): boolean {
  return uiBlockType === BlockType.Output;
}

export function isSingletonBlockType(uiBlockType?: UiBlockType): boolean {
  return isBlockType(uiBlockType) && SingletonBlockTypes.has(uiBlockType);
}

export interface ConnectingNode {
  nodeId: string;
  handleId?: string | null;
  handleType: 'source' | 'target';
}

export interface AddNodeProps {
  connectingNode?: ConnectingNode;
  parentId?: string;
  position: { x: number; y: number };
}

export enum NodeConfigTab {
  Configuration = 'Configuration',
  Analytics = 'Analytics',
  Documentation = 'Documentation',
  LastTestRunOutput = 'LastTestRunOutput',
  /** Only used when resourceFirstArchitectureDemo flag is on */
  Resources = 'Resources',
}
